import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Ticket, LayoutDashboard, Calendar, Scan, LogOut, Menu, X, Shield, Settings, Mail, HelpCircle, Handshake } from 'lucide-react';
import { useState, useEffect } from 'react';
import LanguageSelector from './LanguageSelector';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Layout({ children }) {
  const { user, tenant, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    fetchUnreadCount();
    // Poll every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/dashboard/unread-messages-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Calendar, label: 'Eventi', path: '/events' },
    { icon: Scan, label: 'Scanner', path: '/scanner' },
    { icon: Settings, label: 'Impostazioni', path: '/settings' },
    { icon: HelpCircle, label: 'Aiuto', path: '/help' },
  ];

  // Add Admin link for super_admin
  if (user?.role === 'super_admin') {
    menuItems.unshift(
      { icon: Shield, label: 'Admin', path: '/admin' },
      { icon: Mail, label: 'Newsletter', path: '/newsletter' }
    );
  }

  // Add Partners link for admin and super_admin
  if (user?.role === 'admin' || user?.role === 'super_admin') {
    menuItems.splice(menuItems.length - 2, 0, { icon: Handshake, label: 'Partner', path: '/admin/partners' });
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 bg-blue-600 rounded-xl">
                <Ticket className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">ZonePass</h1>
                {/* Tenant name removed for production branding */}
              </div>
            </div>

            {/* Desktop Menu */}
            <nav data-testid="desktop-navigation" className="hidden xl:flex items-center gap-1">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
                return (
                  <Button
                    key={item.path}
                    variant={isActive ? 'default' : 'ghost'}
                    onClick={() => navigate(item.path)}
                    className="gap-2"
                    data-testid={`nav-${item.label.toLowerCase()}`}
                  >
                    <Icon className="w-4 h-4" />
                    {item.label}
                  </Button>
                );
              })}
            </nav>

            <div className="flex items-center gap-3">
              {/* <LanguageSelector /> */} {/* Temporarily disabled - Italian only */}
              <div className="hidden 2xl:block max-w-40 text-right">
                <p data-testid="desktop-user-name" className="truncate text-sm font-medium text-gray-900">{user?.full_name}</p>
                <p data-testid="desktop-user-email" className="truncate text-xs text-gray-600">{user?.email}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="hidden xl:flex gap-2"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" />
                Esci
              </Button>
              
              {/* Mobile menu button */}
              <Button
                variant="ghost"
                size="sm"
                className="xl:hidden"
                aria-label={mobileMenuOpen ? 'Chiudi menu' : 'Apri menu'}
                aria-expanded={mobileMenuOpen}
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-button"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div data-testid="mobile-navigation" className="xl:hidden max-h-[calc(100vh-4rem)] overflow-y-auto border-t border-gray-200 bg-white">
            <div className="px-4 py-3 space-y-2">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
                return (
                  <Button
                    key={item.path}
                    variant={isActive ? 'default' : 'ghost'}
                    onClick={() => {
                      navigate(item.path);
                      setMobileMenuOpen(false);
                    }}
                    className="w-full justify-start gap-2"
                    data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                  >
                    <Icon className="w-4 h-4" />
                    {item.label}
                  </Button>
                );
              })}
              <div className="pt-3 border-t border-gray-200">
                <p data-testid="mobile-user-name" className="break-words text-sm font-medium text-gray-900 px-3 mb-1">{user?.full_name}</p>
                <p data-testid="mobile-user-email" className="break-all text-xs text-gray-600 px-3 mb-2">{user?.email}</p>
                <Button
                  variant="ghost"
                  onClick={handleLogout}
                  data-testid="mobile-logout-button"
                  className="w-full justify-start gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <LogOut className="w-4 h-4" />
                  Esci
                </Button>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
    </div>
  );
}
